import { Action, OptionValueType, Trigger } from './types';
import Option from './Option';
import { useApp } from '../common/AppProvider';
import { translations } from '../translations/translations';
import useAuthentication from '../common/useAuthentication';
import { apply } from 'json-logic-js';
import { isItemAuthenticated } from './utils';
import { base64SvgToSrc, stringReplace } from '../common/utils';
import { TaskEditAction } from './Task';
import { useId, Dispatch } from 'react';
import { ExButton, ExIcon } from '@boomi/exosphere';

interface Props {
    trigger: Trigger | null;
    action: Action;
    dispatchTask: Dispatch<TaskEditAction>;
    index: number;
    showValidation: boolean;
}

export const getValidationMessage = (action: Action) => {
    for (const validation of action.validations) {
        if (!apply(validation.rules, action.configurations)) {
            return validation.message;
        }
    }

    return null;
};

const ActionComponent = ({ trigger, action, dispatchTask, index, showValidation }: Props) => {
    const { language, connectors, isOnline } = useApp();
    const { onAuthenticate } = useAuthentication(action?.connectorId);
    const id = useId();

    const requiredConfig = action.availableConfigurations.filter((filter) => filter.isRequired);
    const optionalConfig = action.availableConfigurations.filter((filter) => !filter.isRequired);

    const connector = connectors[action.connectorId];

    if (!connector) {
        return;
    }

    const isAuthenticated = isItemAuthenticated(action, connector);

    const onConfigurationChange = (id: string, value: OptionValueType) => {
        dispatchTask({
            type: 'updateAction',
            payload: { index, optionId: id, optionValue: value },
        });
    };

    let validationMessage = null;
    if (showValidation) {
        validationMessage = getValidationMessage(action);
    }

    return (
        <div className="task-item action ta-tile">
            <div className="task-item-title">
                <img
                    src={base64SvgToSrc(connector.icon)}
                    className="icon"
                    title={connector.displayName}
                />
                <h4 className="title lg">{action.name}</h4>
                <ExButton
                    data-testid={`connector-${connector.id}-authenticate`}
                    className="auth-button exo-button"
                    flavor="branded"
                    size="large"
                    type="tertiary"
                    disabled={!isOnline}
                    onClick={() => onAuthenticate(action.scope)}
                >
                    <ExIcon
                        className={isAuthenticated ? 'authenticated' : 'not-authenticated'}
                        icon="Authenticated"
                        slot="prefix"
                        hide-browser-tooltip
                    />
                    {isAuthenticated
                        ? translations[language].TASK_authenticated
                        : translations[language].TASK_authenticate}
                </ExButton>
                <ExButton
                    className="action-button exo-button-action"
                    flavor="risky"
                    size="large"
                    type="tertiary"
                    onClick={() =>
                        dispatchTask({
                            type: 'deleteAction',
                            payload: { index },
                        })
                    }
                >
                    <ExIcon icon="trash" hide-browser-tooltip />
                </ExButton>
                {validationMessage && (
                    <p className="validation-message-bar text xs">{validationMessage}</p>
                )}
            </div>
            <div className="action-config">
                {action.externalConfiguration && (
                    <div className="external-config">
                        <p>
                            {stringReplace(
                                translations[language].TASK_external_configuration_info,
                                connector.displayName,
                            )}
                        </p>
                        <pre>{action.externalConfiguration}</pre>
                        {action.externalConfigurationHelpUrl && (
                            <p>
                                {translations[language].TASK_external_configuration_link}{' '}
                                <a
                                    href={action.externalConfigurationHelpUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {connector.displayName} Documentation
                                </a>
                                {'.'}
                            </p>
                        )}
                    </div>
                )}
                {action.availableConfigurations.length > 0 && (
                    <>
                        {!!requiredConfig.length && (
                            <div className="required-config">
                                {requiredConfig.map((configuration) => (
                                    <Option
                                        key={id + configuration.id}
                                        option={configuration}
                                        value={action.configurations[configuration.id]}
                                        onChange={onConfigurationChange}
                                        outputs={trigger?.outputs}
                                        showValidation={showValidation}
                                        language={language}
                                    />
                                ))}
                            </div>
                        )}
                        {!!optionalConfig.length && (
                            <details className="optional-config">
                                <summary className="optional-config-trigger label md">
                                    <ExIcon
                                        icon="direction-arrowhead-down"
                                        className="optional-config-trigger-icon"
                                    />
                                    {translations[language].ACTION_CONFIG_optional_config}
                                </summary>
                                <div className="details-content">
                                    {optionalConfig.map((configuration) => (
                                        <Option
                                            key={id + configuration.id}
                                            option={configuration}
                                            value={action.configurations[configuration.id]}
                                            onChange={onConfigurationChange}
                                            outputs={trigger?.outputs}
                                            showValidation={showValidation}
                                            language={language}
                                        />
                                    ))}
                                </div>
                            </details>
                        )}
                    </>
                )}
                {!action.externalConfiguration && !action.availableConfigurations?.length && (
                    <p className="config-message">
                        {translations[language].TASK_no_action_configurations}
                    </p>
                )}
            </div>
        </div>
    );
};

export default ActionComponent;
